import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

const PREFERRED_DATE_LOCALES = ['en-KE', 'en-US'] as const;

function resolveDateLocale(): string {
  try {
    return Intl.DateTimeFormat.supportedLocalesOf(PREFERRED_DATE_LOCALES)[0] ?? 'en-US';
  } catch {
    return 'en-US';
  }
}

const DATE_LOCALE = resolveDateLocale();

function formatWithDateLocale(iso: string, options: Intl.DateTimeFormatOptions): string {
  return new Intl.DateTimeFormat(DATE_LOCALE, options).format(new Date(iso));
}

/** Format an ISO date string to a human-readable date + time. */
export function formatDateTime(iso: string): string {
  return formatWithDateLocale(iso, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Format an ISO date string to date only. */
export function formatDate(iso: string): string {
  return formatWithDateLocale(iso, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/** Return a relative time string (e.g. "2 hours ago"). */
export function formatRelative(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

/** Map a status string to the appropriate badge CSS classes. */
export function statusBadgeClass(status?: string | null): string {
  const map: Record<string, string> = {
    active: 'badge-status badge-granted',
    confirmed: 'badge-status badge-granted',
    granted: 'badge-status badge-granted',
    awaiting_visitor_registration: 'badge-status badge-pending',
    checked_out: 'badge-status badge-visitor',
    used: 'badge-status badge-visitor',
    pending: 'badge-status badge-pending',
    expired: 'badge-status badge-warning',
    cancelled: 'badge-status badge-denied',
    denied: 'badge-status badge-denied',
    declined: 'badge-status badge-denied',
    suspended: 'badge-status badge-warning',
    revoked: 'badge-status badge-denied',
    fulfilled: 'badge-status badge-granted',
    in_progress: 'badge-status badge-visitor',
    rejected: 'badge-status badge-denied',
    // DSAR states (server dsar_status enum)
    submitted: 'badge-status badge-pending',
    processing: 'badge-status badge-visitor',
    failed: 'badge-status badge-denied',
  };
  if (!status) return 'badge-status badge-visitor';
  return map[status.toLowerCase()] ?? 'badge-status badge-visitor';
}

/**
 * Format a raw status value (e.g. `awaiting_visitor_registration`) into a
 * human-readable label (e.g. `Awaiting Visitor Registration`). Centralises the
 * inconsistent per-page formatting of status strings.
 */
export function formatStatusLabel(status?: string | null): string {
  if (!status) return '—';
  return status
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/** Format a time-only string (HH:MM). */
export function formatTime(time: string): string {
  const [h, m] = time.split(':');
  const hour = parseInt(h ?? '0', 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const h12 = hour % 12 || 12;
  return `${h12}:${m} ${ampm}`;
}

/** Format duration in minutes to a readable string. */
export function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

/** Day names for schedule display. */
export const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
